import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import Select from 'react-select'

import backArrow from '../../assets/images/back-arrow.svg?url'
import Pencil from '../../assets/images/pencil.svg'

import { getBotById } from 'Src/reselect/bots'
import { saveFlow, saveandPublishFlow, setActiveFlow, deleteFlow, updateFlowProperties } from 'Actions/flow'
import { getBotFlows } from 'Selectors/bots'
import { getFlowHasUnsavedChanges } from 'Selectors/flow'
import { getIsMultiFlowFeatureSupported } from 'Selectors/account'
import useOutsideClickListener from 'LegacyUtils/hooks/useOutsideClickListener'
import EditFlowModal from './EditFlowModal'
import HelpText from 'Design/components/HelpText'

const Options = ({ botId, flowId, flowsCount, openEditFlowModal }) => {
  const [show, setShow] = useState(false)
  const ref = useRef()
  const dispatch = useDispatch()
  const closeHandler = useCallback(() => setShow(false), [setShow])

  const canDelete = flowsCount > 1

  useOutsideClickListener(ref, closeHandler, show)

  const defaultClick = useCallback(
    (e) => {
      e.preventDefault()
      setShow(!show)
    },
    [show]
  )

  const handleDeleteClick = () => {
    const yes = window.confirm('Are you sure to delete the flow?')

    if (yes) {
      dispatch(deleteFlow({ botId, flowId }))
    }
  }

  const handleEditFlowClick = () => {
    openEditFlowModal()
  }

  return (
    <div className='options-dots pr-2' onClick={defaultClick}>
      {show && (
        <div className='dropdown-menu' ref={ref}>
          <span className='dropdown-menu-arrow' />
          <a className='dropdown-item' onClick={handleEditFlowClick}>
            Edit Flow
          </a>
          {canDelete && (
            <a className='dropdown-item' onClick={handleDeleteClick}>
              Delete flow
            </a>
          )}
        </div>
      )}
    </div>
  )
}

const FlowName = ({ name = 'Default', botId, flowId }) => {
  const [flowName, setFlowName] = useState(name)
  const [isEditing, setIsEditing] = useState(false)

  const dispatch = useDispatch()

  useEffect(() => {
    setFlowName(name)
  }, [name])

  const handleClick = () => {
    setIsEditing(true)
  }

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter') {
      return
    }

    update()
  }

  const handleBlur = () => {
    update()
  }

  const update = () => {
    setIsEditing(false)

    dispatch(
      updateFlowProperties({
        name: flowName,
        flowId,
        botId
      })
    )
  }

  const handleInputChange = (e) => {
    setFlowName(e.target.value)
  }

  if (isEditing) {
    return (
      <label className='autosize-input flow-name-input' data-value={flowName}>
        <input
          type='text'
          contentEditable
          value={flowName}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          maxLength='50'
          autoFocus
        />
      </label>
    )
  }

  return (
    <div className='title mr-3' onClick={handleClick}>
      {flowName}{' '}
      <span className='pointer'>
        <Pencil height='17' width='17' />
      </span>
    </div>
  )
}

const FlowNav = ({ botId, flowId, container, embedToken }) => {
  const [isEditFlowModalOpen, setIsEditFlowModalOpen] = useState(false)
  let botsListLink = '/dashboard'
  if (container === 'newDesign') {
    botsListLink = '/b/bots'
  }

  const bot = useSelector((state) => getBotById(state, botId))
  const flows = useSelector((state) => getBotFlows(state, botId))
  const isMultiFlowFeatureSupported = useSelector(getIsMultiFlowFeatureSupported)
  const showUnsavedMessage = useSelector(getFlowHasUnsavedChanges)

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const flowSettings = useMemo(() => flows.find((flow) => flow.id === flowId)?.settings || {}, [flowId, flows])

  const flowSelectOptions = useMemo(() => {
    return flows
      .slice()
      .reverse()
      .map((flow) => {
        return {
          value: flow.id,
          label: flow.settings?.name || 'Default'
        }
      })
  }, [flows])

  const handleTest = () => {
    window.open(`/test/${embedToken}?flow_id=${flowId}`, '_blank')
  }

  const handleSaveandPublish = useCallback(() => {
    dispatch(saveandPublishFlow({ flowId, botId }))
  }, [dispatch, flowId, botId])

  const handleSave = useCallback(() => {
    dispatch(saveFlow({ flowId, botId }))
  }, [dispatch, flowId, botId])

  const handleFlowChange = (option) => {
    dispatch(
      setActiveFlow({
        botId,
        flowId: option.value
      })
    )

    console.log(option.value)
    navigate('../flows/' + option.value)
  }

  const handleOpenEditFlowModal = useCallback(() => {
    setIsEditFlowModalOpen(true)
  }, [])

  const hideEditFlowModal = useCallback(() => {
    setIsEditFlowModalOpen(false)
  }, [])

  const selectedFlow = { value: flowId, label: flowSettings.name || 'Default' }

  return (
    <div className='flow-nav'>
      {isEditFlowModalOpen && (
        <EditFlowModal isOpen hide={hideEditFlowModal} botId={botId} flowId={flowId} settings={flowSettings} />
      )}
      <Link className='back-button mr-3' to={botsListLink}>
        <img src={backArrow} />
      </Link>
      {container === 'newDesign' && <FlowName name={flowSettings.name} botId={botId} flowId={flowId} />}
      {!container && <div className='title mr-3'>{bot.name}</div>}

      {isMultiFlowFeatureSupported && (
        <div className=''>
          <Select
            className='flow-select'
            options={flowSelectOptions}
            onChange={handleFlowChange}
            value={selectedFlow}
          />
        </div>
      )}

      <div className='options d-flex align-items-center'>
        {showUnsavedMessage && <HelpText className='unsaved-text'>You have unsaved changes</HelpText>}
        <button className='button-primary outline mr-2' onClick={handleTest}>
          Test
        </button>
        <button className='button-primary outline mr-2' onClick={handleSaveandPublish}>
          Save & publish
        </button>
        <button className='button-primary mr-2' onClick={handleSave}>
          Save
        </button>
        {isMultiFlowFeatureSupported && (
          <Options
            botId={botId}
            flowId={flowId}
            flowsCount={flows.length}
            openEditFlowModal={handleOpenEditFlowModal}
          />
        )}
      </div>
    </div>
  )
}

export default FlowNav
